import {
  prisma,
  OrderType,
  ParticipantType,
  ParticipantGroupType,
  FormEntityType,
  FormEntity,
  InvitationStatus,
  AvailabilitiesModel,
  NetworkingModel,
  Profile,
  Prisma,
} from "client";
import { createDefaultAvailabilities } from "../availability";
import { createDefaultNetworkingSettings } from "../networkingSettings";
import { getEventLanguagesByEventIdOrEventSlug } from "../language";
import { createParticipantFormTemplate } from "../formTemplates";
import jwt from "jsonwebtoken";
import { createExhibitor, updateExhibitor } from "services/exhibitor";
import { createSponsor, updateSponsor } from "services/sponsor";
import { findOrCreateScopedUser } from "services/user/scopedUser";

type FieldConfig = {
  value: string;
  label: string;
  validationRule?: string;
  type?: string;
};

export const DEFAULT_FIELDS: readonly FieldConfig[] = [
  { value: "name", label: "Name", validationRule: undefined, type: "text" },
  {
    value: "email",
    label: "Email",
    validationRule: "EmailValidation",
    type: "email",
  },
  {
    value: "phoneNumber",
    label: "Phone Number",
    validationRule: undefined,
    type: "phone",
  },
  {
    value: "qrReferenceNumber",
    label: "Ref number (QR)",
    validationRule: undefined,
    type: "text",
  },
  {
    value: "company",
    label: "company",
    validationRule: undefined,
    type: "text",
  },
  {
    value: "countryCode",
    label: "Country Code",
    validationRule: undefined,
    type: "text",
  },
  {
    value: "jobTitle",
    label: "Job Title",
    validationRule: undefined,
    type: "text",
  },
  {
    value: "image",
    label: "User Photo",
    validationRule: undefined,
    type: "user_photo",
  },
  { value: "bio", label: "Bio", validationRule: undefined, type: "text" },
  { value: "gender", label: "Gender", validationRule: undefined, type: "text" },
  { value: "age", label: "Age", validationRule: undefined, type: "text" },
  {
    value: "birthday",
    label: "Date of Birth",
    validationRule: undefined,
    type: "date",
  },
  { value: "state", label: "State", validationRule: undefined, type: "text" },
  { value: "city", label: "City", validationRule: undefined, type: "text" },
  {
    value: "country",
    label: "Country",
    validationRule: undefined,
    type: "text",
  },
  {
    value: "socialMedia",
    label: "socialMedia",
    validationRule: undefined,
    type: "object",
  },
] as const;

// Helper Types
export interface ProfileData {
  email?: string;
  phoneNumber?: string;
  [key: string]: any;
}

export interface CreateProfileV2Params {
  data: ProfileData;
  eventId: string;
  orderId?: string;
  participantGroupId?: string;
  whiteLabelId?: string;
  orderType?: OrderType;
  anonymous?: boolean;
}

export interface CreateProfileV2Result {
  profile: Profile;
  token?: string;
  entityType?: ParticipantType;
}

export function normalizeProfileFieldValue(key: string, value: unknown) {
  if (key !== "qrReferenceNumber" || typeof value !== "string") return value;
  return value.trim() || null;
}

// Constants
export const MEETING_FORM_TEMPLATE = {
  json: {
    logoPosition: "right",
    pages: [
      {
        name: "page1",
        elements: [
          {
            type: "dropdown",
            name: "question1",
            title: "Meeting Location",
            title_ar: "مكان الاجتماع",
            choices: [
              { value: "Item 1", text: "Coffee Area", text_ar: "منطقة المقهى" },
              { value: "Item 2", text: "Booth", text_ar: "جناح العرض" },
              {
                value: "Item 3",
                text: "Networking Area",
                text_ar: "منطقة التواصل",
              },
              { value: "Item 4", text: "Other", text_ar: "غير ذلك" },
            ],
          },
          {
            type: "text",
            name: "question4",
            visibleIf: "{question1} = 'Item 4'",
            title: "Custom Location",
            title_ar: "حدد مكان الاجتماع",
          },
          {
            type: "dropdown",
            name: "question2",
            title: "Reason",
            title_ar: "سبب طلب الاجتماع",
            choices: [
              {
                value: "Item 1",
                text: "Need a mentoring consultation",
                text_ar: "للحصول على نصائح مهنية",
              },
              {
                value: "Item 2",
                text: "Chat and idea exchange",
                text_ar: "للتحدث وتبادل الأفكار",
              },
            ],
          },
          {
            type: "comment",
            name: "question3",
            title: "Description",
            title_ar: "وصف اضافي",
          },
        ],
      },
    ],
  },
  numberOfQuestions: 4,
} as const;

// Helper Functions
export const separateProfileFields = (data: ProfileData) => {
  const allowedFields = new Set(DEFAULT_FIELDS.map((field) => field.value));
  const profileData: Record<string, any> = {};
  const extraData: Record<string, any> = {};

  for (const [key, value] of Object.entries(data)) {
    if (key === "phone" && value != null) {
      profileData["phoneNumber"] = String(value);
    } else if (key === "national_id" && value != null) {
      profileData["nationalId"] = String(value);
    }
    if (allowedFields.has(key)) {
      profileData[key] = normalizeProfileFieldValue(key, value);
    } else if (key !== "password" && key !== "confirmPassword") {
      extraData[key] = value;
    }
  }

  if (Object.keys(extraData).length > 0) {
    profileData.extraData = extraData;
  }

  return profileData;
};

export const findExistingProfile = async (
  email: string | undefined | null,
  phoneNumber: string | undefined | null,
  eventId: string,
  userId?: string
) => {
  if (!email && !phoneNumber) {
    return null;
  }

  if (!/^[a-f\d]{24}$/i.test(eventId)) {
    return null;
  }

  const conditions: Prisma.ProfileWhereInput = { eventId };

  if (userId) {
    conditions.userId = userId;
  } else {
    if (email) {
      conditions.email = email;
    }
    if (phoneNumber) {
      conditions.phoneNumber = phoneNumber;
    }
  }
  return await prisma.profile.findFirst({
    where: conditions,
  });
};

export const determineEntityType = (
  participantGroupTypes: ParticipantGroupType[]
): ParticipantType => {
  if (participantGroupTypes.includes(ParticipantGroupType.EXHIBITORS)) {
    return ParticipantType.EXHIBITOR;
  }
  if (participantGroupTypes.includes(ParticipantGroupType.SPEAKERS)) {
    return ParticipantType.SPEAKER;
  }
  if (participantGroupTypes.includes(ParticipantGroupType.SPONSORS)) {
    return ParticipantType.SPONSOR;
  }
  if (participantGroupTypes.includes(ParticipantGroupType.MEDIA)) {
    return ParticipantType.MEDIA;
  }
  if (participantGroupTypes.includes(ParticipantGroupType.SUPPORT)) {
    return ParticipantType.SUPPORT;
  }
  return ParticipantType.VISITOR;
};

export const createOrUpdateProfile = async (
  profile: any,
  profileData: Record<string, any>,
  eventId: string,
  orderId?: string,
  participantGroupId?: string,
  orderType?: OrderType,
  userId?: string,
  anonymous?: boolean
) => {
  if (!profile) {
    return await prisma.profile.create({
      data: {
        ...profileData,
        event: { connect: { id: eventId } },
        ...(orderId && { orders: { connect: { id: orderId } } }),
        ...(participantGroupId && {
          participantGroups: { connect: { id: participantGroupId } },
        }),
        user: userId ? { connect: { id: userId } } : undefined,
        anonymous: anonymous ?? false,
      },
    });
  }

  if (orderType === OrderType.INVITATION) {
    return await prisma.profile.update({
      where: { id: profile.id },
      data: profileData,
    });
  }

  return profile;
};

export const setupParticipantFeatures = async (
  profile: any,
  participantGroupIds: string[],
  eventId: string,
  whiteLabelId: string | undefined,
  userId?: string,
  participantImportId?: string
) => {
  const participantGroups = await prisma.participantGroup.findMany({
    where: { id: { in: participantGroupIds } },
  });

  const participantGroupTypes = participantGroups.map((group) => group.type);
  const entityType = determineEntityType(participantGroupTypes);
  let user = null;
  if (userId) {
    user = await prisma.user.findUnique({
      where: { id: userId },
    });
  }

  if (!user) {
    // If no user found by id, try to create/find by email from profile
    const email = (profile?.email as string) || undefined;
    const name = (profile?.name as string) || undefined;
    if (!email || !whiteLabelId) {
      throw new Error("User not found for profile and no email to create one");
    }
    const scoped = await findOrCreateScopedUser({
      email,
      whiteLabelId,
      name,
      image: typeof profile?.image === "string" ? profile.image : null,
      participantImportId,
    });
    user = await prisma.user.findUnique({ where: { id: scoped.id } });
    if (!user) {
      throw new Error("User not found for profile and no email to create one");
    }
  }
  // Ticket checkout can create the User without linking Profile.userId.
  if (profile?.id && user?.id && !profile.userId) {
    try {
      await prisma.profile.update({
        where: { id: profile.id },
        data: { user: { connect: { id: user.id } } },
      });
      profile.userId = user.id;
    } catch {
      // ponytail: copy-link can resolve via profile id; don't fail purchase
    }
  }
  // If entity is EXHIBITOR or SPONSOR, create or update the company entity
  if (
    entityType === ParticipantType.EXHIBITOR ||
    entityType === ParticipantType.SPONSOR
  ) {
    const eventLanguages = await getEventLanguagesByEventIdOrEventSlug(eventId);
    const translations = eventLanguages?.map((t) => ({
      name:
        profile?.company || profile?.name || user?.name || profile?.email || "",
      languageId: t.id,
    }));

    const entityPayload = {
      email: (profile?.email || user?.email) as string,
      phone: (profile?.phoneNumber || "") as string,
      contactName: (user?.name || profile?.name || "") as string,
      contactJobTitle: profile?.jobTitle,
    };

    if (entityType === ParticipantType.EXHIBITOR) {
      const existingExhibitor = profile?.email
        ? await prisma.exhibitor.findFirst({
            where: { email: profile.email, eventId },
          })
        : null;

      if (existingExhibitor) {
        const response = await updateExhibitor(
          existingExhibitor.id,
          entityPayload,
          translations,
          participantGroupIds?.[0]
        );
        if (response?.error) {
          throw new Error(response.error);
        }

        const token = jwt.sign(
          {
            entityId: existingExhibitor.id,
            email: existingExhibitor.email,
            entityType: ParticipantGroupType.EXHIBITORS,
          },
          process.env.SECRET_HASH_PASSWORD as string
        );

        await prisma.participantInvitation.upsert({
          where: {
            entityId_entityType_email: {
              entityId: existingExhibitor.id,
              entityType: ParticipantType.EXHIBITOR,
              email: profile.email as string,
            },
          },
          update: { token, status: InvitationStatus.PENDING },
          create: {
            email: profile.email as string,
            status: InvitationStatus.PENDING,
            entityType: ParticipantType.EXHIBITOR,
            entityId: existingExhibitor.id,
            token,
            isOwner: true,
          },
        });

        return { token, entityType: ParticipantType.EXHIBITOR };
      }

      const response = await createExhibitor(
        entityPayload,
        eventId,
        translations,
        whiteLabelId as string,
        user,
        participantGroupIds?.[0]
      );
      if (!("token" in response)) {
        throw new Error(
          "error" in response
            ? String(response.error)
            : "Failed to create exhibitor"
        );
      }
      return { token: response.token, entityType };
    }

    if (entityType === ParticipantType.SPONSOR) {
      const existingSponsor = profile?.email
        ? await prisma.sponsor.findFirst({
            where: { email: profile.email, eventId },
          })
        : null;

      if (existingSponsor) {
        const response = await updateSponsor(
          existingSponsor.id,
          entityPayload,
          translations,
          participantGroupIds?.[0]
        );
        if (response?.error) {
          throw new Error(response.error);
        }

        const token = jwt.sign(
          {
            entityId: existingSponsor.id,
            email: existingSponsor.email,
            entityType: ParticipantGroupType.SPONSORS,
          },
          process.env.SECRET_HASH_PASSWORD as string
        );

        await prisma.participantInvitation.upsert({
          where: {
            entityId_entityType_email: {
              entityId: existingSponsor.id,
              entityType: ParticipantType.SPONSOR,
              email: profile.email as string,
            },
          },
          update: { token, status: InvitationStatus.PENDING },
          create: {
            email: profile.email as string,
            status: InvitationStatus.PENDING,
            entityType: ParticipantType.SPONSOR,
            entityId: existingSponsor.id,
            token,
            isOwner: true,
          },
        });

        return { token, entityType: ParticipantType.SPONSOR };
      }

      const response = await createSponsor(
        entityPayload,
        eventId,
        translations,
        whiteLabelId as string,
        participantGroupIds?.[0],
        user
      );
      if (!("token" in response)) {
        throw new Error(
          "error" in response
            ? String(response.error)
            : "Failed to create sponsor"
        );
      }
      return { token: response.token, entityType };
    }
  }

  const existingParticipantInvitation = profile?.email
    ? await prisma.participantInvitation.findFirst({
        where: {
          email: profile.email as string,
          entityType,
          entityId: user?.id,
        },
      })
    : null;

  if (existingParticipantInvitation && user) {
    const token = jwt.sign(
      {
        entityId: user.id,
        email: profile.email,
        entityType,
      },
      process.env.SECRET_HASH_PASSWORD as string
    );

    await prisma.participantInvitation.update({
      where: { id: existingParticipantInvitation.id },
      data: {
        token,
        status: InvitationStatus.PENDING,
        participantImportId,
      },
    });

    return { token, entityType };
  }

  // Otherwise, create defaults for USER entity
  await Promise.all([
    createDefaultAvailabilities({
      entityId: user.id as string,
      entityModel: AvailabilitiesModel.USER,
      eventId,
    }),
    createDefaultNetworkingSettings(
      eventId,
      user.id as string,
      NetworkingModel.USER,
      user,
      participantGroupIds?.[0]
    ),
  ]);

  // Create participant form template
  const eventLanguages = await getEventLanguagesByEventIdOrEventSlug(eventId);
  const userFormTranslations = eventLanguages?.map((t) => ({
    name: user.name || "",
    languageId: t.id,
  }));

  await createParticipantFormTemplate(
    {
      whiteLabelId: whiteLabelId as string,
      eventId,
      createdById: user.id,
    },
    userFormTranslations,
    MEETING_FORM_TEMPLATE,
    {
      entityID: profile.id,
      type: FormEntityType.MEETING,
    } as FormEntity
  );

  // Generate JWT token
  const token = jwt.sign(
    {
      entityId: user.id,
      email: profile.email,
      entityType,
    },
    process.env.SECRET_HASH_PASSWORD as string
  );

  // Upsert participant invitation
  await prisma.participantInvitation.upsert({
    where: {
      entityId_entityType_email: {
        entityId: user.id,
        entityType,
        email: profile.email as string,
      },
    },
    update: {
      status: InvitationStatus.PENDING,
      entityType,
      entityId: user.id,
      token,
      isOwner: true,
      participantImportId,
    },
    create: {
      status: InvitationStatus.PENDING,
      entityType,
      entityId: user.id,
      token,
      isOwner: true,
      email: profile.email as string,
      participantImportId,
    },
  });

  return { token, entityType };
};
